/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable quotes */
import { Injectable, inject } from '@angular/core';
import { applyTransaction, arrayAdd, arrayRemove, arrayUpdate, EntityStore, StoreConfig } from '@datorama/akita';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import {
  AccountHeldByType,
  AssetLiabilitiesModel,
  AssetModel,
  AssetReoModel,
  CreditModel,
  ExpenseModel,
  LiabilityModel,
} from '../../cmg-1003/models';
import { SessionStore } from '../../core/state';
import { Cmg1003ApplicationInfoQuery } from '../application-info/application-info.query';
import { iStoreActions } from '../core/store-actions.interface';
import { isNullish, nonNullish } from '../../shared/utils';

function createInitialState(): AssetLiabilitiesModel {
  return <AssetLiabilitiesModel>{};
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'cmg-1003-asset-liabilities-store', idKey: 'applicationId' })
export class Cmg1003AssetsStore extends EntityStore<AssetLiabilitiesModel> implements iStoreActions<AssetLiabilitiesModel> {
  private readonly _sessionStore = inject(SessionStore);

  private _appInfoQueryInstance: Cmg1003ApplicationInfoQuery = null;
  private _initValue: AssetLiabilitiesModel[] = [];

  constructor() {
    super(createInitialState());
  }

  // Because of akitaPreAddEntity, this Store doesn't have preInitialize.
  override akitaPreAddEntity(assetsModel: AssetLiabilitiesModel) {
    (assetsModel.realEstateOwnedList || []).forEach((reo) => {
      reo.isPitiOverridden = reo.pitiOverrideValue >= 0;
      reo.isVacancyFactorOverridden = nonNullish(reo.vacancyFactorOverrideValue) ? reo.vacancyFactorOverrideValue >= 0 : false;
      reo.isLinkedToLiabilities = (reo.linkedLiabilityIds || []).length > 0;
    });
    const expensesAsLiabilities = [];
    (assetsModel.expenses || []).forEach((expense) => {
      expensesAsLiabilities.push(ExpenseModel.ToLiability(expense));
    });
    if (expensesAsLiabilities.length > 0) {
      assetsModel.liabilitiesList = [...expensesAsLiabilities, ...assetsModel.liabilitiesList].map((liability: LiabilityModel) => {
        if (liability.liabilityType === 2 /* Other Liability */) {
          /* Other liabilities is not part off the credit report.
            Payment Left months can be considered but not part of the Paid Off liabilities
            So, the indicator will be always set as No */
          liability.toBePaidOff = false;
        }
        return liability;
      });
    }
    this._initValue.push(JSON.parse(JSON.stringify(assetsModel)));
    return assetsModel;
  }

  override akitaPreUpdateEntity(_: Readonly<AssetLiabilitiesModel>, nextEntity: any): AssetLiabilitiesModel {
    this._sessionStore.update({ lastUpdatedDateTime: Date.now() });
    return nextEntity;
  }

  get appInfoQuery(): Cmg1003ApplicationInfoQuery {
    if (this._appInfoQueryInstance === null) {
      this._appInfoQueryInstance = Cmg1003ApplicationInfoQuery.instance;
    }
    return this._appInfoQueryInstance;
  }

  deleteAsset(appId: number, id: number) {
    this.update(appId, (assetModel: AssetLiabilitiesModel) => {
      return {
        ...assetModel,
        assetsList:
          id < 0 ? arrayRemove(assetModel.assetsList, id, 'assetId') : arrayUpdate(assetModel.assetsList, id, { isDeleted: true }, 'assetId'),
      };
    });
  }

  deleteCredit(appId: number, id: number) {
    this.update(appId, (assetModel: AssetLiabilitiesModel) => {
      return {
        ...assetModel,
        creditsList:
          id < 0 ? arrayRemove(assetModel.creditsList, id, 'creditId') : arrayUpdate(assetModel.creditsList, id, { isDeleted: true }, 'creditId'),
      };
    });
  }

  deleteLiability(appId: number, id: number) {
    this.update(appId, (assetModel: AssetLiabilitiesModel) => {
      // Check for and remove any linked REOs
      const updatedModel = this.removeLiabilityFromLinkedREO(id, assetModel);

      return {
        ...updatedModel,
        liabilitiesList:
          id < 0
            ? arrayRemove(updatedModel.liabilitiesList, id, 'liabilityId')
            : arrayUpdate(updatedModel.liabilitiesList, id, { isDeleted: true }, 'liabilityId'),
      };
    });
  }

  /**
   * Search all REOs for a linked liability based on the liabilityId provided
   * @param liabilityId ID of the liability to search for
   * @param liabilityBorrowerId If specified, the liability will only be found when the REO borrower ID does NOT match
   * @param assetModel Store model
   */
  getREOLinkedToLiability(
    liabilityId: number,
    liabilityBorrowerId?: number | undefined,
    assetModel: AssetLiabilitiesModel = (this.getValue() as any).entities[this.appInfoQuery.getActiveId()] as AssetLiabilitiesModel,
  ): AssetReoModel | undefined {
    return assetModel.realEstateOwnedList.find(
      (reo) =>
        (isNullish(liabilityBorrowerId) || reo.borrowerId !== liabilityBorrowerId) &&
        reo.linkedLiabilityIds.some((l) => {
          return l === liabilityId;
        }),
    );
  }

  /**
   * By providing a liability ID, the liability will be removed from any linked REOs
   * @param liabilityId ID of the liability to search for and remove from REOs
   * @param assetModel Store model
   * @param liabilityBorrowerId If specified, the liability will only be removed when the REO borrower ID does NOT match
   */
  removeLiabilityFromLinkedREO(
    liabilityId: number,
    assetModel: AssetLiabilitiesModel,
    liabilityBorrowerId?: number | undefined,
  ): AssetLiabilitiesModel {
    let linkedReo = this.getREOLinkedToLiability(liabilityId, liabilityBorrowerId, assetModel);
    if (linkedReo) {
      // Remove the liability ID from the linked REO
      linkedReo = Object.assign({}, linkedReo, { linkedLiabilityIds: linkedReo.linkedLiabilityIds?.filter((t) => t !== liabilityId) });
      // Reference to the deleted liability
      const deletedLiability = assetModel.liabilitiesList?.find((liability) => liability.liabilityId === liabilityId);
      // Update all lien positions on other the REO linked liabilities
      let updatedLiabilities = assetModel.liabilitiesList ?? [];
      if (deletedLiability) {
        updatedLiabilities = updatedLiabilities.map((liability) => {
          // Reset properties on deleted liability
          if (liability.liabilityId === deletedLiability.liabilityId) {
            return {
              ...liability,
              isLinkedToReo: false,
              reoId: null,
              lienPosition: undefined,
            } as LiabilityModel;
          }
          // If the liability is linked to the same REO and the lien position
          // is greater than the deleted liability lien position, decrement it
          if (
            liability.lienPosition > deletedLiability.lienPosition &&
            linkedReo.linkedLiabilityIds?.length &&
            linkedReo.linkedLiabilityIds.includes(liability.liabilityId)
          ) {
            return {
              ...liability,
              lienPosition: liability.lienPosition - 1,
            } as LiabilityModel;
          }
          return liability;
        });
      }
      return {
        ...assetModel,
        realEstateOwnedList: arrayUpdate(assetModel.realEstateOwnedList, linkedReo.reoId, linkedReo, 'reoId'),
        liabilitiesList: updatedLiabilities,
      } as AssetLiabilitiesModel;
    } else {
      return {
        ...assetModel,
      } as AssetLiabilitiesModel;
    }
  }

  deleteREO(appId: number, reoId: number) {
    applyTransaction(() => {
      //TODO: Find a better way to get this, but for now this works and gets the liabilities for the given app.
      const store = this.getValue() as any;
      const liabilities = store.entities[appId].liabilitiesList?.filter((t) => t.reoId === reoId);
      (liabilities || []).forEach((liability) => {
        this.updateLiability(appId, Object.assign({}, liability, { reoId: null }));
      });

      this.update(appId, (assetModel: AssetLiabilitiesModel) => {
        return {
          ...assetModel,
          realEstateOwnedList:
            reoId < 0
              ? arrayRemove(assetModel.realEstateOwnedList, reoId, 'reoId')
              : arrayUpdate(assetModel.realEstateOwnedList, reoId, { isDeleted: true }, 'reoId'),
        };
      });
    });
  }

  updateAsset(appId: number, updatedAsset: AssetModel) {
    const stateFunction =
      updatedAsset.assetId === 0
        ? (assetModel: AssetLiabilitiesModel) => {
            updatedAsset.assetId = -CmgUiUtilities.getUniqueNumber();
            return {
              ...assetModel,
              assetsList: arrayAdd(assetModel.assetsList, updatedAsset),
            };
          }
        : (assetModel: AssetLiabilitiesModel) => {
            return {
              ...assetModel,
              assetsList: arrayUpdate(assetModel.assetsList, updatedAsset.assetId, updatedAsset, 'assetId'),
            };
          };
    this.update(appId, stateFunction);
  }

  updateStatementCompleted(statementCompleted: number) {
    const appId = this.appInfoQuery.getActiveId();
    this.update(appId, (assetModel: AssetLiabilitiesModel) => {
      return {
        ...assetModel,
        statementCompleted: statementCompleted,
      };
    });
  }

  updateCredit(appId: number, updatedCredit: CreditModel) {
    const stateFunction =
      updatedCredit.creditId === 0
        ? (assetModel: AssetLiabilitiesModel) => {
            updatedCredit.creditId = -CmgUiUtilities.getUniqueNumber();
            return {
              ...assetModel,
              creditsList: arrayAdd(assetModel.creditsList, updatedCredit),
            };
          }
        : (assetModel: AssetLiabilitiesModel) => {
            return {
              ...assetModel,
              creditsList: arrayUpdate(assetModel.creditsList, updatedCredit.creditId, updatedCredit, 'creditId'),
            };
          };
    this.update(appId, stateFunction);
  }

  updateLiabilityForActiveApp(updatedLiability: LiabilityModel) {
    this.updateLiability(this.appInfoQuery.getActiveId() as number, updatedLiability);
  }

  updateLiability(appId: number, updatedLiability: LiabilityModel) {
    const stateFunction =
      updatedLiability.liabilityId === 0
        ? (assetModel: AssetLiabilitiesModel) => {
            updatedLiability.liabilityId = -CmgUiUtilities.getUniqueNumber();
            return {
              ...assetModel,
              liabilitiesList: arrayAdd(assetModel.liabilitiesList, updatedLiability),
            };
          }
        : (assetModel: AssetLiabilitiesModel) => {
            return {
              ...assetModel,
              liabilitiesList: arrayUpdate(assetModel.liabilitiesList, updatedLiability.liabilityId, updatedLiability, 'liabilityId'),
            };
          };
    this.update(appId, stateFunction);
  }

  updateReo(appId: number, updatedReo: AssetReoModel) {
    updatedReo.isLinkedToLiabilities = updatedReo.linkedLiabilityIds.length > 0;
    const isAdd = updatedReo.reoId === 0;
    updatedReo.reoId = isAdd ? -CmgUiUtilities.getUniqueNumber() : updatedReo.reoId;
    const stateFunction = (assetModel: AssetLiabilitiesModel) => {
      const liabilitiesList = [];
      assetModel.liabilitiesList?.forEach((liability) => {
        if (updatedReo.linkedLiabilityIds.find((t) => t === liability.liabilityId)) {
          liability = Object.assign({}, liability, { reoId: updatedReo.reoId });
        } else {
          if (liability.reoId === updatedReo.reoId) {
            liability = Object.assign({}, liability, { reoId: null });
          }
        }
        liabilitiesList.push(liability);
      });
      return {
        ...assetModel,
        liabilitiesList: liabilitiesList,
        realEstateOwnedList: isAdd
          ? arrayAdd(assetModel.realEstateOwnedList, updatedReo)
          : arrayUpdate(assetModel.realEstateOwnedList, updatedReo.reoId, updatedReo, 'reoId'),
      };
    };
    this.update(appId, stateFunction);
  }

  updateLiabilityLienPosition(liabilityId: number, lienPosition: number) {
    const appId = this.appInfoQuery.getActiveId();
    this.update(appId, (assetModel: AssetLiabilitiesModel) => {
      const liabilityModel = Object.assign(
        {},
        assetModel.liabilitiesList.find((t) => t.liabilityId === liabilityId),
      );
      liabilityModel.lienPosition = lienPosition;
      return {
        ...assetModel,
        liabilitiesList: arrayUpdate(assetModel.liabilitiesList, liabilityModel.liabilityId, Object.assign({}, liabilityModel), 'liabilityId'),
      };
    });
  }

  linkReoAndLiability(reoId: number, liabilityId: number, link = true) {
    const appId = this.appInfoQuery.getActiveId();
    this.update(appId, (assetModel: AssetLiabilitiesModel) => {
      const reoModel = Object.assign(
        {},
        assetModel.realEstateOwnedList.find((t) => t.reoId === reoId),
      );
      if (!reoModel.linkedLiabilityIds) {
        reoModel.linkedLiabilityIds = [];
      }
      const liabilityModel = Object.assign(
        {},
        assetModel.liabilitiesList.find((t) => t.liabilityId === liabilityId),
      );

      if (link) {
        reoModel.linkedLiabilityIds.push(liabilityId);
        liabilityModel.reoId = reoId;
      } else {
        reoModel.linkedLiabilityIds = [...reoModel.linkedLiabilityIds.filter((t) => t !== liabilityId)];
        reoModel.isLinkedToLiabilities = reoModel.linkedLiabilityIds.length > 0;
        liabilityModel.reoId = null;
        liabilityModel.lienPosition = undefined;
      }
      return {
        ...assetModel,
        realEstateOwnedList: arrayUpdate(assetModel.realEstateOwnedList, reoModel.reoId, reoModel, 'reoId'),
        liabilitiesList: arrayUpdate(assetModel.liabilitiesList, liabilityModel.liabilityId, liabilityModel, 'liabilityId'),
      };
    });
  }

  get initialValue() {
    return this._initValue;
  }
}
